import React from 'react'

import styled, {
  elementProps,
  labelWhenSmallProps,
} from '../../constants/theme'
import { FormElementProps } from './FormElementParts'

const SelectElement: React.FC<FormElementProps> = ({
  ariaLabelledBy,
  disabled,
  includeBlank,
  isFocused,
  isRequired,
  label,
  name,
  onChange,
  options,
  value,
}) => {
  const elRef = React.useRef<HTMLSelectElement>(null)
  const callChange = React.useCallback(
    (val) => {
      if (onChange) {
        onChange({ name, value: val })
      }
    },
    [name, onChange],
  )
  const handleSingleSelectChange = (e) => {
    e.preventDefault()
    callChange(e.target.value)
  }

  let renderedOptions = options ? [...options] : []
  if (includeBlank) {
    renderedOptions.unshift({ label: includeBlank || '', value: '' })
  }

  React.useEffect(() => {
    if (elRef.current) {
      callChange(elRef.current.value)
    }
  }, [callChange])
  if (!options || !options.length) {
    return null
  }

  return (
    <Select
      aria-label={label}
      aria-labelledby={ariaLabelledBy}
      autoFocus={isFocused}
      disabled={disabled}
      name={name}
      onChange={handleSingleSelectChange}
      ref={elRef}
      required={isRequired}
      value={value || undefined}
    >
      {renderedOptions.map((option) => {
        return (
          <option
            aria-selected={value === option.value}
            key={`option_${option.value}`}
            value={option.value}
          >
            {option.label}
          </option>
        )
      })}
    </Select>
  )
}

const Select = styled.select(
  {
    '& ~ label': {
      ...labelWhenSmallProps,
    },
    '&::-ms-expand': {
      display: 'none',
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
    ...elementProps,
    appearance: 'none',
  },
  ({ theme }) => ({
    backgroundColor: theme.background,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.borderRadiusSmall,
    color: theme.text,
    fontFamily: theme.fontFamily,
    padding: theme.paddingSmall,

    '&:hover': {
      border: `1px solid ${theme.primary}`,
    },

    '&:focus': {
      border: `1px solid ${theme.primary}`,
      boxShadow: `0 0 0 1px ${theme.primary}`,
    },
  }),
)
Select.displayName = 'Select'

export default SelectElement
